import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";

interface MarkdownMessageProps {
  content: string;
  className?: string;
  /** Set to true if the content is primarily Arabic */
  isRtl?: boolean;
}

const allowedElements = [
  "p", "strong", "em", "ul", "ol", "li", "h2", "h3", "br", "del", "code", "table", "thead", "tbody", "tr", "th", "td",
] as const;

export function MarkdownMessage({ content, className, isRtl }: MarkdownMessageProps) {
  if (!content) return null;

  return (
    <section
      dir={isRtl ? "rtl" : "auto"}
      lang={isRtl ? "ar" : "en"}
      className={cn(
        "break-words text-slate-800 antialiased",
        "space-y-1.5 lg:space-y-2",
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        allowedElements={[...allowedElements]}
        unwrapDisallowed
        components={{
          h2: ({ children }) => (
            <h2 className="mt-3.5 mb-1.5 text-[13px] font-semibold tracking-[-0.015em] text-slate-900 sm:text-[14px] lg:text-[15px]">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="mt-2.5 mb-1 text-[12px] font-semibold tracking-[-0.01em] text-slate-800 sm:text-[13px] lg:text-[14px]">
              {children}
            </h3>
          ),
          p: ({ children }) => (
            <p className={cn("mb-2.5 text-[13px] text-slate-800 [text-wrap:pretty] sm:text-[14px] lg:text-[15px]", isRtl ? "leading-8 lg:leading-9" : "leading-7 lg:leading-8")}>
              {children}
            </p>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-slate-900">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic text-slate-700">{children}</em>
          ),
          code: ({ children }) => (
            <code className="rounded-md bg-secondary px-1.5 py-0.5 font-mono text-[10.5px] text-foreground sm:text-[11px]">
              {children}
            </code>
          ),
          ul: ({ children }) => (
            <ul
              className={cn(
                "mb-2 list-inside space-y-1.5",
                isRtl ? "list-disc pe-1 ps-0 text-right" : "list-disc ps-4 pe-0 text-left",
              )}
            >
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol
              className={cn(
                "mb-2 list-inside space-y-1.5",
                isRtl ? "list-decimal pe-1 ps-0 text-right" : "list-decimal ps-4 pe-0 text-left",
              )}
            >
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className={cn("text-[13px] text-slate-800 marker:text-slate-400 sm:text-[14px] lg:text-[15px]", isRtl ? "leading-8 lg:leading-9" : "leading-7 lg:leading-8")}>
              {children}
            </li>
          ),
          table: ({ children }) => (
            <div className="mb-2 overflow-x-auto rounded-[1rem] border border-slate-200/55 bg-white/82 shadow-[0_8px_24px_rgba(15,23,42,0.04)]">
              <table className="w-full min-w-[18rem] border-collapse text-left text-[11px] text-slate-800 sm:text-[12px] lg:text-[13px]">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-slate-100/90">{children}</thead>,
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => <tr className="border-b border-slate-200/50 last:border-b-0">{children}</tr>,
          th: ({ children }) => (
            <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
              {children}
            </th>
          ),
          td: ({ children }) => <td className="px-3 py-2.5 align-top text-[11px] leading-6 text-slate-700 [unicode-bidi:plaintext] sm:text-[12px] lg:text-[13px] lg:leading-7">{children}</td>,
        }}
      >
        {content}
      </ReactMarkdown>
    </section>
  );
}
